import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { formValue } from '#lib/form-value';
import { mePatchSchema } from '@tasks/contracts/users';
import { ApiError } from '#lib/api-client';
import { useMe, useUpdateMe } from '#features/users/api';
import { Button } from '#components/ui/button';
import { EmbeddedProfile } from '#features/users/components/embedded-profile';
import { useHostMode } from '#lib/host/host-mode-context';

const Field = ({
  name,
  label,
  type = 'text',
  defaultValue,
}: {
  name: string;
  label: string;
  type?: string;
  defaultValue?: string;
}) => (
  <label className="block text-sm">
    {label}
    <input
      name={name}
      type={type}
      defaultValue={defaultValue}
      className="mt-1 w-full rounded-sm border border-input px-3 py-2 bg-background"
    />
  </label>
);

const ProfilePage = () => {
  const me = useMe();
  const update = useUpdateMe();
  const [message, setMessage] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = formValue(form, 'password');
    const parsed = mePatchSchema.safeParse({
      name: formValue(form, 'name'),
      handle: formValue(form, 'handle'),
      ...(password === '' ? {} : { password, currentPassword: formValue(form, 'currentPassword') }),
    });
    if (!parsed.success) {
      setMessage('Check the details before saving.');
      return;
    }
    try {
      await update.mutateAsync(parsed.data);
      setMessage(
        password === ''
          ? 'Profile saved.'
          : 'Password changed. Your other sessions have been signed out.',
      );
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'Could not save.');
    }
  };

  if (!me.data) return null;
  return (
    <main className="mx-auto max-w-md p-6">
      <h1 className="text-xl">Profile</h1>
      <form onSubmit={(e) => void onSubmit(e)} className="mt-4 space-y-3">
        <Field name="name" label="Name" defaultValue={me.data.name} />
        <Field name="handle" label="Handle" defaultValue={me.data.handle} />
        <fieldset className="space-y-3 border-t border-border pt-3">
          <legend className="text-sm text-muted-foreground">Change password</legend>
          <Field name="currentPassword" label="Current password" type="password" />
          <Field name="password" label="New password" type="password" />
        </fieldset>
        {message === null ? null : <output className="block text-sm">{message}</output>}
        <Button type="submit" disabled={update.isPending}>
          Save
        </Button>
      </form>
    </main>
  );
};

// Framed by Vitto the account is Vitto's: the page is the name alone (EmbeddedProfile); the password and handle form is the product's own.
const ProfileRoute = () =>
  useHostMode().kind === 'embedded' ? <EmbeddedProfile /> : <ProfilePage />;

export const Route = createFileRoute('/_authed/settings/profile')({ component: ProfileRoute });
